import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Button, Checkbox, ConfirmDialog, ErrorState, Field, Input, Modal, Switch, Textarea } from '@bb/ui';
import { ApiError, type ExtraDefinitionInput, type ExtraDefinitionView, type ExtrasChannel, type ExtrasCheck, type ExtraType } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { TypedExtrasForm } from '../composer/shared/TypedExtrasForm.tsx';

const CHANNELS: Array<[ExtrasChannel, string]> = [
  ['content_card', 'Content Cards'],
  ['push', 'Push'],
  ['in_app', 'In-app messages'],
  ['banner', 'Banners'],
];
const TYPES: ExtraType[] = ['string', 'enum', 'enum_list', 'number', 'boolean', 'url', 'deeplink', 'json'];
const errorText = (error: unknown) => (error instanceof ApiError ? (error.problem.issues as Array<{ message: string }> | undefined)?.map((i) => i.message).join('; ') || error.message : 'Something went wrong.');
const list = (raw: string) =>
  raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

type Draft = { key: string; type: ExtraType; values: string; required: boolean; description: string; deprecated: boolean; whenKey: string; whenValues: string };
const emptyDraft: Draft = { key: '', type: 'string', values: '', required: false, description: '', deprecated: false, whenKey: '', whenValues: '' };
const toDraft = (d: ExtraDefinitionView): Draft => ({ key: d.key, type: d.type, values: d.enumValues.join(', '), required: d.required, description: d.description ?? '', deprecated: d.deprecated, whenKey: d.appliesWhen?.key ?? '', whenValues: d.appliesWhen?.in.join(', ') ?? '' });
const toInput = (d: Draft): ExtraDefinitionInput => ({
  type: d.type,
  enumValues: d.type === 'enum' || d.type === 'enum_list' ? list(d.values) : [],
  required: d.required,
  description: d.description.trim() || null,
  deprecated: d.deprecated,
  appliesWhen: d.whenKey ? { key: d.whenKey, in: list(d.whenValues) } : null,
});

/**
 * Settings → Message extras (BB-58): the typed key-value extras each channel's messages carry. Environment managers
 * define keys, types, allowed values and conditions; everyone sees the schema and how the composer will show it.
 */
export function ExtrasSchemaPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/extras' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/extras' });
  const queryClient = useQueryClient();
  const canManage = usePermissions().has('env.manage');
  const [channel, setChannel] = useState<ExtrasChannel>('content_card');
  const key = ['extras-schema', environmentId, channel];
  const schema = useQuery({ queryKey: key, queryFn: () => api.extrasSchema(environmentId, channel) });
  const [editing, setEditing] = useState<{ id: string | null; draft: Draft } | null>(null);
  const [removing, setRemoving] = useState<ExtraDefinitionView | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['extras-schema', environmentId] });
  const save = useMutation({
    mutationFn: ({ id, draft }: { id: string | null; draft: Draft }) => (id ? api.updateExtra(environmentId, id, toInput(draft)) : api.createExtra(environmentId, { channel, key: draft.key.trim(), ...toInput(draft) })),
    onSuccess: async () => {
      setEditing(null);
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteExtra(environmentId, id),
    onSuccess: async () => {
      setRemoving(null);
      await refresh();
    },
  });
  const strict = useMutation({ mutationFn: (on: boolean) => api.setExtrasStrict(environmentId, channel, on), onSuccess: (view) => queryClient.setQueryData(key, view) });

  // Composer preview: the form and the server's verdict on its values.
  const [sample, setSample] = useState<Record<string, string>>({});
  const [check, setCheck] = useState<ExtrasCheck | null>(null);
  useEffect(() => {
    let live = true;
    const timer = setTimeout(() => {
      void api.validateExtras(environmentId, channel, sample).then((result) => live && setCheck(result), () => live && setCheck(null));
    }, 250);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [api, environmentId, channel, sample]);

  const edit = (patch: Partial<Draft>) => setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...patch } } : e));
  const others = (schema.data?.items ?? []).filter((d) => d.key !== editing?.draft.key);

  return (
    <section>
      <h1>Message extras</h1>
      <p className="hint">Key-value extras the apps read from messages (e.g. which screen shows a card). Typed keys get a form in the composer, and saving checks their values.</p>
      <div className="filters">
        <select
          aria-label="Channel"
          value={channel}
          onChange={(e) => {
            setChannel(e.target.value as ExtrasChannel);
            setSample({});
          }}
        >
          {CHANNELS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        {schema.data &&
          (canManage ? (
            <Switch label="Refuse keys outside the schema" checked={schema.data.strict} onCheckedChange={(on) => strict.mutate(on)} disabled={strict.isPending} />
          ) : (
            <span className="hint">{schema.data.strict ? 'Strict: keys outside the schema are refused.' : 'Keys outside the schema are allowed with a warning.'}</span>
          ))}
        {canManage && (
          <Button variant="primary" onClick={() => setEditing({ id: null, draft: emptyDraft })}>
            Add key
          </Button>
        )}
      </div>
      {schema.isPending ? (
        <p aria-busy="true">Loading extras…</p>
      ) : schema.isError ? (
        <ErrorState title="Extras could not be loaded" message={errorText(schema.error)} onRetry={() => void schema.refetch()} />
      ) : schema.data.items.length === 0 ? (
        <p className="empty-state">No typed extras for this channel yet: messages carry free key-value pairs.</p>
      ) : (
        <table className="table" aria-label="Extras">
          <thead>
            <tr>
              <th scope="col">Key</th>
              <th scope="col">Type</th>
              <th scope="col">Values</th>
              <th scope="col">Required</th>
              <th scope="col">Description</th>
              {canManage && (
                <th scope="col">
                  <span className="bb-visually-hidden">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {schema.data.items.map((d) => (
              <tr key={d.id}>
                <td>
                  <code>{d.key}</code>
                  {d.deprecated && <span className="tag tag--neutral"> Deprecated</span>}
                </td>
                <td>{d.type}</td>
                <td>{d.enumValues.join(', ') || '—'}</td>
                <td>{d.required ? (d.appliesWhen ? `when ${d.appliesWhen.key} is ${d.appliesWhen.in.join(' or ')}` : 'yes') : d.appliesWhen ? `only when ${d.appliesWhen.key} is ${d.appliesWhen.in.join(' or ')}` : 'no'}</td>
                <td>{d.description ?? ''}</td>
                {canManage && (
                  <td>
                    <Button variant="ghost" aria-label={`Edit ${d.key}`} onClick={() => setEditing({ id: d.id, draft: toDraft(d) })}>
                      Edit
                    </Button>
                    <Button variant="ghost" aria-label={`Remove ${d.key}`} onClick={() => setRemoving(d)}>
                      Remove
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {schema.data && (
        <section aria-label="Composer preview" className="panel">
          <h2>Composer preview</h2>
          <p className="hint">How the composer shows these extras; try values to see what saving would say and what the apps receive.</p>
          <TypedExtrasForm schema={schema.data} value={sample} onChange={setSample} check={check} />
          {check && (
            <p role="status" className={check.ok ? 'hint' : 'field-error'}>
              {check.ok ? (check.warnings.length ? `Can be saved, with ${check.warnings.length} warning(s).` : 'Can be saved.') : `Cannot be saved: ${check.errors.length} problem(s).`}
            </p>
          )}
          <dl className="facts">
            <dt>extras</dt>
            <dd>
              <code aria-label="Raw extras">{JSON.stringify(sample)}</code>
            </dd>
            <dt>typedExtras</dt>
            <dd>
              <code aria-label="Typed extras">{JSON.stringify(check?.typed ?? {})}</code>
            </dd>
          </dl>
        </section>
      )}

      <Modal
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        title={editing?.id ? `Edit ${editing.draft.key}` : 'Add key'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button variant="primary" loading={save.isPending} disabled={!editing?.draft.key.trim()} onClick={() => editing && save.mutate(editing)}>
              Save
            </Button>
          </>
        }
      >
        {editing && (
          <>
            <Field label="Key">
              <Input value={editing.draft.key} disabled={editing.id !== null} onChange={(e) => edit({ key: e.target.value })} spellCheck={false} />
            </Field>
            <Field label="Type">
              <select value={editing.draft.type} onChange={(e) => edit({ type: e.target.value as ExtraType })}>
                {TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            {(editing.draft.type === 'enum' || editing.draft.type === 'enum_list') && (
              <Field label="Allowed values" hint="Comma-separated">
                <Input value={editing.draft.values} onChange={(e) => edit({ values: e.target.value })} />
              </Field>
            )}
            <Checkbox label="Required" checked={editing.draft.required} onCheckedChange={(on) => edit({ required: on })} />
            <Field label="Only when key" hint="Leave empty when the key always applies">
              <select value={editing.draft.whenKey} onChange={(e) => edit({ whenKey: e.target.value })}>
                <option value="">—</option>
                {others.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.key}
                  </option>
                ))}
              </select>
            </Field>
            {editing.draft.whenKey && (
              <Field label="has one of" hint="Comma-separated">
                <Input value={editing.draft.whenValues} onChange={(e) => edit({ whenValues: e.target.value })} />
              </Field>
            )}
            <Field label="Description">
              <Textarea value={editing.draft.description} onChange={(e) => edit({ description: e.target.value })} />
            </Field>
            <Checkbox label="Deprecated" checked={editing.draft.deprecated} onCheckedChange={(on) => edit({ deprecated: on })} />
            {save.isError && <p role="alert">{errorText(save.error)}</p>}
          </>
        )}
      </Modal>
      <ConfirmDialog open={removing !== null} onOpenChange={(open) => !open && setRemoving(null)} title={`Remove ${removing?.key ?? ''}?`} confirmLabel="Remove" busy={remove.isPending} onConfirm={() => removing && remove.mutate(removing.id)}>
        Messages keep their values; the key becomes a free key-value pair again.
      </ConfirmDialog>
    </section>
  );
}
